import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useDrugs } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useDropdownNav } from "@/hooks/useDropdownNav";

/**
 * Per-row drug search box for the taper grid (only used in per-strength mode).
 * A compact search input bound to /drugs?q=… that fills the taper row's
 * drug fields (name/strength/composition/formulation/drugId) on selection.
 * Self-contained: manages its own query string and dropdown visibility so
 * each row has an independent box (typing in row 1 doesn't affect row 2).
 *
 * Extracted 2026-11-01 (Batch 7 Wave 3) from the PrescriptionTable and
 * EditMedicineDialog copies (the pure draft helpers live in taperDraft.ts;
 * this file stays component-only so react-refresh/only-export-components
 * stays clean).
 *
 * `debounceMs` (default 250) preserves the two historical behaviours this
 * component was extracted from: PrescriptionTable debounces the /drugs?q=
 * request (one per typing pause) while EditMedicineDialog queried per
 * keystroke — pass debounceMs={0} there to keep that behaviour. The
 * dropdown itself is always driven by the live `q`.
 */
export function TaperRowDrugSearch({
  rowKey,
  value,
  onPick,
  debounceMs = 250,
}: {
  rowKey: string;
  value: string;
  onPick: (d: { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }) => void;
  debounceMs?: number;
}) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  // Keep the local query in sync if the row's name changes elsewhere (e.g.
  // when the per-strength toggle seeds the row with the draft-level drug).
  useEffect(() => { setQ(value); }, [value, rowKey]);
  // Debounced search query — the /drugs?q= request fires once per typing
  // pause at debounceMs=250, or per keystroke at 0 (the edit dialog's
  // original behaviour). The dropdown stays driven by the live `q`
  // (open state, "keep as entered" text); only the network query lags.
  const debouncedQ = useDebouncedValue(q, debounceMs);
  const queryQ = debounceMs > 0 ? debouncedQ : q;
  const { data: drugsData, isLoading } = useDrugs(queryQ.length >= 3 ? queryQ : "");
  const drugs = (drugsData && "rows" in drugsData ? drugsData.rows : Array.isArray(drugsData) ? drugsData : []) as { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }[];

  // Dropdown items: up to 10 drug matches + 1 "No match — keep" entry.
  const taperResults = drugs.slice(0, 10);
  const taperListCount = taperResults.length + 1;

  const { highlight: taperHighlight, highlightRef: taperHighlightRef, onKeyDown: onTaperSearchKeyDown } = useDropdownNav({
    count: open && q.length >= 3 && !isLoading ? taperListCount : 0,
    onSelect: (i) => {
      if (i < taperResults.length) onPick(taperResults[i]);
      else onPick({ id: "", name: q, composition: null, formulation: null, strength: "" });
      setOpen(false);
    },
    inputRef: ref,
    onEscape: () => setOpen(false),
  });

  return (
    <div className="relative">
      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
      <Input
        ref={ref}
        className="h-8 pl-7 text-xs"
        value={q}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          // Keep the row's name in sync with what the user typed, so a
          // custom (non-DB) name still saves correctly when they don't pick.
          onPick({ id: "", name: v, composition: null, formulation: null, strength: "" });
          setOpen(v.length >= 3);
        }}
        onFocus={() => q.length >= 3 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onTaperSearchKeyDown}
        placeholder="Search drug / strength"
      />
      {open && q.length >= 3 && (
        <div className="visible-scrollbar absolute z-50 mt-1 max-h-52 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
          {isLoading ? (
            <div className="p-2"><Skeleton className="h-5" /></div>
          ) : drugs.length === 0 ? (
            <button
              type="button"
              ref={taperHighlight === 0 ? taperHighlightRef : undefined}
              className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs hover:bg-cream-200 dark:hover:bg-dark-700 ${
                taperHighlight === 0 ? "bg-cream-200 dark:bg-dark-700" : ""
              }`}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); }}
            >
              <Search size={12} className="text-muted" /> No match — keep "{q}"
            </button>
          ) : (
            taperResults.map((d, idx) => (
              <button
                key={d.id}
                type="button"
                ref={idx === taperHighlight ? taperHighlightRef : undefined}
                className={`flex w-full flex-col px-3 py-1.5 text-left text-xs hover:bg-cream-200 dark:hover:bg-dark-700 ${
                  idx === taperHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                }`}
                onMouseDown={(e) => { e.preventDefault(); onPick(d); setOpen(false); }}
              >
                <span className="font-medium text-ink dark:text-cream-50">{d.name}</span>
                <span className="text-[10px] text-muted">
                  {[d.composition, d.formulation, d.strength].filter(Boolean).join(" · ")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}